import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { BackLink } from '../components/ui/BackLink';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { getTutorial, TUTORIALS } from '../content/tutorials';
import { getAuthor } from '../content/authors';
import { tutorialJsonLd, tutorialUrl } from '../content/tutorialJsonLd';
import { AuthorByline } from '../components/tutorials/AuthorByline';

/**
 * A single tutorial — /tutorials/{slug}. Crawlable (no auth), Medium-style readable layout, with the
 * SEO/AEO/GEO structure: a direct-answer summary, ordered steps, supporting prose, an FAQ, a key-terms
 * glossary, and Article + HowTo + FAQPage + BreadcrumbList structured data.
 */
const TutorialPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const t = getTutorial(slug);
  const author = t ? getAuthor(t.authorId) : undefined;

  useDocumentMeta({
    title: t ? t.metaTitle : 'Tutorial — Wisetree AI',
    description: t?.metaDescription,
    canonical: t ? tutorialUrl(t.slug) : 'https://wisetree.ai/tutorials',
    jsonLd: t ? tutorialJsonLd(t, author) : undefined,
  });

  if (!t) {
    return (
      <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <BackLink to="/tutorials" label="Tutorials" />
          <div className="mt-8">
            <h1 className="text-2xl font-bold">Tutorial not found</h1>
            <p className="mt-2 text-secondary-500">That tutorial doesn’t exist. <Link to="/tutorials" className="underline">Browse all tutorials</Link>.</p>
          </div>
        </div>
      </div>
    );
  }

  const related = t.related.map((s) => TUTORIALS.find((x) => x.slug === s)).filter(Boolean) as typeof TUTORIALS;

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      {/* Medium-style column: generous width + readable body type. */}
      <div className="max-w-[680px] mx-auto px-6 py-10">
        <BackLink to="/tutorials" label="Tutorials" />

        <article className="mt-6">
          <div className="text-xs uppercase tracking-wide text-secondary-400">{t.segment} · {t.useCase}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold leading-tight tracking-tight">{t.title}</h1>
          {author && <div className="mt-5"><AuthorByline author={author} readingTimeMin={t.readingTimeMin} updated={t.updated} /></div>}

          {/* AEO: 40-60-word direct answer, liftable as a snippet. */}
          <p className="mt-6 text-lg leading-relaxed text-secondary-700 dark:text-secondary-200 border-l-2 border-secondary-300 dark:border-secondary-700 pl-4">{t.tldr}</p>

          <p className="mt-6 text-[1.05rem] leading-8 text-secondary-700 dark:text-secondary-300">{t.intro}</p>

          {/* Ordered steps → HowTo. */}
          <h2 className="mt-10 text-xl font-bold">Step by step</h2>
          <ol className="mt-4 space-y-5">
            {t.steps.map((s, i) => (
              <li key={s.title} className="flex gap-4">
                <span className="flex items-center justify-center w-7 h-7 rounded-full border border-secondary-300 dark:border-secondary-700 text-sm font-semibold shrink-0">{i + 1}</span>
                <div>
                  <h3 className="font-semibold text-secondary-900 dark:text-white">{s.title}</h3>
                  <p className="mt-1 text-[1.05rem] leading-8 text-secondary-600 dark:text-secondary-300">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>

          {t.sections.map((sec) => (
            <section key={sec.heading} className="mt-10">
              <h2 className="text-xl font-bold">{sec.heading}</h2>
              {sec.body.map((p, i) => <p key={i} className="mt-3 text-[1.05rem] leading-8 text-secondary-600 dark:text-secondary-300">{p}</p>)}
            </section>
          ))}

          {/* GEO: key-terms glossary for clean entity association. */}
          <section className="mt-10">
            <h2 className="text-xl font-bold">Key terms</h2>
            <dl className="mt-3 space-y-2">
              {t.keyTerms.map((k) => (
                <div key={k.term} className="text-[1.02rem] leading-7">
                  <dt className="inline font-semibold text-secondary-900 dark:text-white">{k.term}. </dt>
                  <dd className="inline text-secondary-600 dark:text-secondary-300">{k.def}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* AEO: FAQ → FAQPage. */}
          <section className="mt-10">
            <h2 className="text-xl font-bold">FAQ</h2>
            <div className="mt-3 space-y-4">
              {t.faq.map((f) => (
                <div key={f.q}>
                  <h3 className="font-semibold text-secondary-900 dark:text-white">{f.q}</h3>
                  <p className="mt-1 text-[1.02rem] leading-7 text-secondary-600 dark:text-secondary-300">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-10 border-t border-secondary-100 dark:border-secondary-800 pt-6">
            <Link to="/" className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 text-sm font-medium hover:bg-black dark:hover:bg-secondary-100 transition-colors">
              Try it on Wisetree
            </Link>
          </div>

          {related.length > 0 && (
            <section className="mt-10 border-t border-secondary-100 dark:border-secondary-800 pt-6">
              <h2 className="text-sm font-semibold text-secondary-400 uppercase tracking-wide">Keep reading</h2>
              <div className="mt-3 space-y-2">
                {related.map((r) => (
                  <Link key={r.slug} to={`/tutorials/${r.slug}`} className="block group">
                    <span className="font-medium text-secondary-900 dark:text-white group-hover:underline">{r.title}</span>
                    <span className="block text-sm text-secondary-500">{r.description}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </div>
  );
};

export default TutorialPage;
